import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ShieldAlert, RefreshCw, Loader2 } from "lucide-react";
import { requireAdminRole } from "@/lib/api/admin-users.functions";

/**
 * Pathless layout for /portal/admin/*.
 *
 * Role-check policy:
 *  - We DO NOT use `beforeLoad` redirects. A single transient server-fn
 *    failure (cold start, network blip) was hiding the entire admin console
 *    from legitimate admins. Every admin server fn already calls
 *    `assertAdmin(context)` against `has_role(_, 'admin')`, so access is safe.
 *  - We do a soft client-side check and render a clear inline error if it
 *    fails — admins always see the console shell + a "Retry" button instead
 *    of being silently bounced back to /portal.
 */
export const Route = createFileRoute("/_authenticated/portal/admin")({
  component: AdminLayout,
});

function AdminLayout() {
  const verify = useServerFn(requireAdminRole);
  const { data, status, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-role-check"],
    queryFn: () => verify(),
    retry: 1,
    staleTime: 5 * 60_000,
  });

  if (status === "pending") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Verifying admin access…
      </div>
    );
  }

  if (status === "error" || !data?.ok) {
    const msg = (error as any)?.message ?? "We could not verify your admin role.";
    const forbidden = /forbidden/i.test(String(msg));
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div className="flex-1 space-y-2">
              <p className="font-semibold">
                {forbidden
                  ? "This account does not have the admin role."
                  : "Admin role check failed."}
              </p>
              <p>
                {forbidden ? (
                  <>
                    Sign in with an authorised owner account
                    (<code>nkadimengdanny7@gmail.com</code> or{" "}
                    <code>nkadimengdanny14@gmail.com</code>) to manage the
                    console.
                  </>
                ) : (
                  <>
                    The role-check request didn't complete. The admin console
                    is still safe — every admin action re-checks your role
                    server-side. You can retry the check or continue; you'll
                    only be blocked if you really aren't an admin.
                  </>
                )}
              </p>
              <p className="text-xs text-amber-800/80 break-all">{String(msg)}</p>
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="inline-flex items-center gap-1 rounded-md border border-amber-700/40 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                >
                  <RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />
                  Retry check
                </button>
                <Link
                  to="/portal"
                  className="rounded-md border border-amber-700/40 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  Back to client portal
                </Link>
              </div>
            </div>
          </div>
        </div>
        {/* Still render the outlet — server fns will gate actions individually. */}
        <Outlet />
      </div>
    );
  }

  return <Outlet />;
}
